import {
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
  faFax,
  faFile,
  faLayerGroup,
  faList,
  faPrint,
  faShareFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  API_RESOURCES,
  PRIORITY_BADGE_BORDER_COLORS,
  PRIORITY_BADGE_COLORS,
  PRIORITY_BADGE_TEXT_COLORS,
} from 'src/utils/constants';
import { getMachineTypeFromUrl } from 'src/utils/mlineUtils';
import { getPriorityLabel } from 'src/utils/stringUtils';
import { getRouteURI, getUuid } from 'src/utils/url';

import routes from '../../../../utils/routes';
import { getRunStatusBadgeStyles, STATUS_LABELS_OVERRIDES } from './_run-header';

/**
 * A single row in the compact bar. Header is icon + label + chevron; tap
 * to inline-expand a body with the section's essentials.
 */
const CompactBarRow = ({ icon, label, isExpanded, onToggle, badgeContent, children }) => (
  <div className={`run-header-compact-bar__row${isExpanded ? ' run-header-compact-bar__row--expanded' : ''}`}>
    <button
      type='button'
      className='run-header-compact-bar__row-header'
      aria-expanded={isExpanded}
      onClick={onToggle}
    >
      <span className='run-header-compact-bar__row-icon icon-container'>
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className='run-header-compact-bar__row-label'>{label}</span>
      {badgeContent ? (
        <span className='run-header-compact-bar__row-badge-slot'>{badgeContent}</span>
      ) : null}
      <FontAwesomeIcon
        icon={isExpanded ? faChevronUp : faChevronDown}
        className='run-header-compact-bar__row-chevron'
      />
    </button>
    <div className='run-header-compact-bar__row-body-wrapper' data-open={isExpanded}>
      <div className='run-header-compact-bar__row-body-inner'>
        <div className='run-header-compact-bar__row-body'>{children}</div>
      </div>
    </div>
  </div>
);

CompactBarRow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  icon: PropTypes.object.isRequired,
  label: PropTypes.node.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  badgeContent: PropTypes.node,
  children: PropTypes.node,
};

CompactBarRow.defaultProps = {
  badgeContent: null,
  children: null,
};

const Field = ({ label, value }) => (
  <div className='run-header-compact-bar__field'>
    <span className='run-header-compact-bar__field-label'>{label}</span>
    <span className='run-header-compact-bar__field-value'>{value}</span>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

/**
 * Fixed-position compact bar following the iOS Large Title / Material
 * collapsing top bar pattern. The full cards are rendered in normal flow
 * at the top of the page and scroll away naturally; this bar fades in at
 * viewport top once the cards have scrolled out of view, providing a
 * lightweight, always-reachable summary while the user is reading work
 * instructions below.
 *
 * Each row is tap-to-expand inline, showing the section's essentials.
 * For full details (action buttons, document lists, etc.) the user
 * scrolls back to the top of the page.
 */
const RunHeaderCompactBar = ({
  visible,
  run,
  workstation,
  buildData,
  additionalDocuments,
  estMaterialWeight,
  estMaterialWeightUnits,
  runPrinterType,
  currentGroupIndex,
  totalGroups,
}) => {
  const [expanded, setExpanded] = useState(null);

  const toggle = (key) => setExpanded((prev) => (prev === key ? null : key));

  const runStatusBadgeStyles = getRunStatusBadgeStyles(run);
  const runStatusLabel = STATUS_LABELS_OVERRIDES[run.status] || run.status;
  const runPriorityLabel = getPriorityLabel(run.priority);
  const runPriorityBadgeStyles = run.priority != null
    ? {
      backgroundColor: PRIORITY_BADGE_COLORS[run.priority],
      border: `1px solid ${PRIORITY_BADGE_BORDER_COLORS[run.priority]}`,
      color: PRIORITY_BADGE_TEXT_COLORS[run.priority],
    }
    : null;

  const machineName = getMachineTypeFromUrl(workstation?.uri);
  const isPostProcessor = machineName === API_RESOURCES.POST_PROCESSOR;

  const customFields = buildData?.customFieldEntries || [];
  const layerThickness = buildData?.additive?.layer_thickness;
  const documentsCount = additionalDocuments?.length || 0;
  const runDocumentsCount = run.run_documents?.length || 0;

  const workstationLink = workstation?.uri
    ? getRouteURI(routes.printerMaterial, { uuid: getUuid(workstation.uri) })
    : null;

  const showCounter = totalGroups > 1;
  const safeCurrent = Math.min(Math.max(currentGroupIndex + 1, 1), totalGroups || 1);
  const groupProgress = totalGroups > 0 ? `${(safeCurrent / totalGroups) * 100}%` : '0%';

  return (
    <div
      className={`run-header-compact-bar${visible ? ' run-header-compact-bar--visible' : ''}`}
      inert={!visible ? '' : undefined}
    >
      <div className='run-header-compact-bar__inner'>
        {showCounter ? (
          <div className='run-header-compact-bar__group-status'>
            <div className='run-header-compact-bar__group-counter' aria-live='polite'>
              <span className='run-header-compact-bar__group-label'>Viewing</span>
              <span className='run-header-compact-bar__group-value'>
                <FontAwesomeIcon icon={faLayerGroup} />
                Group {safeCurrent}/{totalGroups}
              </span>
            </div>
            <div
              className='run-header-compact-bar__group-progress'
              aria-label={`Group ${safeCurrent} of ${totalGroups}`}
            >
              <span style={{ width: groupProgress }} />
            </div>
          </div>
        ) : null}
        <CompactBarRow
          icon={faShareFromSquare}
          label={run.customer_id || run.name}
          isExpanded={expanded === 'run'}
          badgeContent={(
            <span
              className='run-header-compact-bar__status-badge'
              style={runStatusBadgeStyles}
            >
              {runStatusLabel}
            </span>
          )}
          onToggle={() => toggle('run')}
        >
          <Field
            label='Status'
            value={(
              <span
                className='run-header-compact-bar__status-badge'
                style={runStatusBadgeStyles}
              >
                {runStatusLabel}
              </span>
            )}
          />
          {runPriorityLabel && runPriorityBadgeStyles ? (
            <Field
              label='Priority'
              value={(
                <span
                  className='run-header-compact-bar__status-badge'
                  style={runPriorityBadgeStyles}
                >
                  {runPriorityLabel}
                </span>
              )}
            />
          ) : null}
          <Field label='Documents' value={runDocumentsCount} />
          <div className='run-header-compact-bar__hint'>
            Scroll to top of page for full details and actions.
          </div>
        </CompactBarRow>

        <CompactBarRow
          icon={faList}
          label='Build Details'
          isExpanded={expanded === 'build'}
          badgeContent={(
            <span className='run-header-compact-bar__doc-badge'>
              <FontAwesomeIcon icon={faFile} />
              {documentsCount}
            </span>
          )}
          onToggle={() => toggle('build')}
        >
          <Field label='Layer Thickness' value={layerThickness || '—'} />
          <Field
            label='Estimated Weight'
            value={
              estMaterialWeight && estMaterialWeightUnits
                ? `${estMaterialWeight} ${estMaterialWeightUnits}`
                : '—'
            }
          />
          {customFields.slice(0, 4).map((field) => (
            <Field
              key={`${field.name}-${field.value || 'empty'}`}
              label={field.name}
              value={field.value || '—'}
            />
          ))}
        </CompactBarRow>

        {workstation ? (
          <CompactBarRow
            icon={isPostProcessor ? faFax : faPrint}
            label={workstation.name}
            isExpanded={expanded === 'workstation'}
            badgeContent={
              workstation?.in_progress_max != null ? (
                <span className='run-header-compact-bar__queue-badge'>
                  {workstation.in_progress?.length || 0}
                  /
                  {workstation.in_progress_max}
                </span>
              ) : null
            }
            onToggle={() => toggle('workstation')}
          >
            {runPrinterType?.name ? (
              <Field label='Printer Type' value={runPrinterType.name} />
            ) : null}
            {workstationLink ? (
              <Link
                to={workstationLink}
                className='run-header-compact-bar__link'
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                <span>View workstation details</span>
              </Link>
            ) : null}
          </CompactBarRow>
        ) : null}
      </div>
    </div>
  );
};

RunHeaderCompactBar.propTypes = {
  visible: PropTypes.bool.isRequired,
  run: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string,
    // eslint-disable-next-line camelcase
    customer_id: PropTypes.string,
    status: PropTypes.string.isRequired,
    priority: PropTypes.number,
    // eslint-disable-next-line camelcase
    run_documents: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  workstation: PropTypes.shape({
    uri: PropTypes.string,
    name: PropTypes.string,
    // eslint-disable-next-line camelcase
    in_progress: PropTypes.arrayOf(PropTypes.string),
    // eslint-disable-next-line camelcase
    in_progress_max: PropTypes.number,
  }),
  buildData: PropTypes.shape({
    customFieldEntries: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ),
    additive: PropTypes.shape({
      // eslint-disable-next-line camelcase
      layer_thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  additionalDocuments: PropTypes.arrayOf(PropTypes.string),
  estMaterialWeight: PropTypes.number,
  estMaterialWeightUnits: PropTypes.string,
  runPrinterType: PropTypes.shape({
    name: PropTypes.string,
  }),
  currentGroupIndex: PropTypes.number,
  totalGroups: PropTypes.number,
};

RunHeaderCompactBar.defaultProps = {
  workstation: null,
  buildData: null,
  additionalDocuments: [],
  estMaterialWeight: null,
  estMaterialWeightUnits: null,
  runPrinterType: null,
  currentGroupIndex: 0,
  totalGroups: 0,
};

export default RunHeaderCompactBar;
